<template>
  <div id="actionPlanSetUp">
    <el-page-header
      :title="$t('common.goBack')"
      :content="null"
      class="mainCard"
      @back="$router.go(-1)"
    />
    <h2
      v-if="$mq === 'mobile' || $mq === 'tablet'"
      class="mHead"
    >
      {{ $t('aside.actionPlan') }}
    </h2>

    <CustomTabs v-loading="loading">
      <template #subtitle>
        <div
          v-if="role_type === 'coach'"
          style="padding: 8px 24px 24px;"
        >
          Please note: This page may be edited by the leader or the coach.
          In order to preserve data,
          please ensure that you are not editing simultaneously on this page.
        </div>
      </template>
      <template
        v-for="(tabName, TKey) in tabOption"
      >
        <!-- Title Slot -->
        <div
          :slot="`title${TKey + 1}`"
          :key="`Tab${TKey}`"
        >
          <div
            v-if="$mq === 'laptop' || $mq === 'desktop'"
            class="title"
          >
            {{ $t('common.lgaLongSingle') }} {{ TKey + 1 }}
          </div>
          <div class="value">
            <b>{{ formattedLga[tabName] }}</b>
          </div>
        </div>

        <!-- Contents Slot -->
        <div
          :slot="`content${TKey + 1}`"
          :key="`TabC${TKey}`"
          v-loading="loading"
        >
          <ActionPlanSetUpTab
            v-if="actionPlans && feedforwards && feedforwards[tabName]"
            :status="actionPlans.status"
            :feedforwards="feedforwards[tabName]"
            :action-plans.sync="actionPlans[tabName]"
          />
        </div>
      </template>
    </CustomTabs>

    <div
      v-if="actionPlans"
      class="mainCard actionsBtn"
    >
      <el-button
        type="primary"
        round
        :disabled="!canSave"
        :class="{mobileButton: ['mobile', 'tablet'].includes($mq)}"
        @click="saveActionPlans"
      >
        {{ $t('common.save') }}
      </el-button>
      <!-- <el-button
        type="primary"
        round
        :disabled="!canSave || !canLaunch"
        :class="{mobileButton: ['mobile', 'tablet'].includes($mq)}"
        @click="publishActionPlans"
      >
        Publish
      </el-button> -->
      <div
        class="wrapper el-button"
        :class="{mobileButton: ['mobile', 'tablet'].includes($mq)}"
      >
        <el-button
          type="primary"
          style="width: 100%;"
          round
          @click="publishActionPlans"
        >
          {{ $t('common.publish') }}
        </el-button>
      </div>
      <!--<el-tooltip
        :disabled="canSave && canLaunch"
        :content="$t('actionPlan.pleaseInputMinimum')"
        placement="top-start"
      >
      </el-tooltip>-->
    </div>

    <UnableDialog
      :show.sync="unableDialog"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import CustomTabs from '@/views/components/customTabs/CustomTabs.vue';
import api from '@/api';
import ActionPlanSetUpTab from './ActionPlanSetUpTab.vue';
import UnableDialog from './widget/UnableDialog.vue';

export default {
  components: {
    ActionPlanSetUpTab,
    CustomTabs,
    UnableDialog,
  },

  data() {
    return {
      actionPlans: null,
      isNext: false, // determine if we are working on current session action plan or next session
      feedforwards: null,
      loading: false,
      unableDialog: false,
      leaderLgas: null,
    };
  },

  computed: {
    ...mapState({
      lgas: (state) => state.myLGAs.lgas,
      role_type: (state) => state.user.user.role_type,
      session: (state) => state.processStatus.process.session || 0,
    }),

    formattedLga() {
      let lga = this.lgas;
      if (this.role_type === 'coach' && this.leaderLgas) {
        lga = this.leaderLgas;
      }
      return lga;
    },

    canSave() {
      // actionPlans.status === 'published' || actionPlans === {}
      if (this.actionPlans.status && this.actionPlans.status === 'published') return false;
      if (this.actionPlans.lga1 === null && this.actionPlans.lga2 === null) return false;

      return true;
    },

    canLaunch() {
      if (this.isNext) return false;

      let lga1 = false;
      let lga2 = false;

      if (this.actionPlans.lga1) {
        lga1 = this.actionPlans.lga1.filter(
          (el) => el.description && el.description.length > 0,
        ).length >= 3;
      }

      if (this.actionPlans.lga2) {
        lga2 = this.actionPlans.lga2.filter(
          (el) => el.description && el.description.length > 0,
        ).length >= 3;
      }

      return lga1 && lga2;
    },

    tabOption() {
      const list = [];

      if (this.feedforwards) {
        Object.keys(this.feedforwards).forEach((key) => {
          list.push(key);
        });
      }

      return list;
    },
  },

  created() {
    if (this.role_type === 'coach') { this.getLeaderLga(); }
    this.getActionPlans();
    this.getFeedforwards();
  },

  methods: {
    ...mapActions('processStatus', [
      'fetchProcess',
    ]),

    async getLeaderLga() {
      try {
        const response = await api.getLeaderLga(this.$route.params.id);
        this.leaderLgas = response.data;
        if (this.leaderLgas.status !== 'published') {
          this.$message.error('LGA needs to be published before');
          this.$router.go(-1);
        }
      } catch (err) {
        console.log(err);
      } finally {
        this.loading = false;
      }
    },

    async getActionPlans() {
      try {
        this.loading = true;
        let currentResponse;
        if (this.role_type === 'coach') {
          currentResponse = await api.getLeaderAp(this.$route.params.id);
        } else {
          currentResponse = await api.getActionPlan();
        }
        let ap;
        if (currentResponse?.data) {
          ap = currentResponse.data;
        }
        if (ap.status === 'published') {
          try {
            let nextResponse;
            if (this.role_type === 'coach') {
              nextResponse = await api.getLeaderApNext(this.$route.params.id);
            } else {
              nextResponse = await api.getNextActionPlan();
            }
            if (nextResponse?.data) {
              ap = nextResponse.data;
            }
          } catch (err) {
            if (err.response.status === 404) {
              ap = currentResponse.data;
              delete ap.status;
            } else if (err.msg) {
              this.$message.error(err.msg);
            }
          } finally {
            this.isNext = true;
          }
        }
        this.actionPlans = ap;
        this.loading = false;
      } catch (err) {
        if (err.msg && err.msg !== 'An error occurred. Please try again later.') {
          this.$message.error(err.msg);
        }
        this.loading = false;
        if (this.role_type === 'leader') {
          this.$router.push({ name: 'dashboard' }).catch((err2) => {});
        } else {
          this.actionPlans = {
            lga1: null,
            lga2: null,
            prev: true,
          };
        }
      }
    },

    async getFeedforwards() {
      let response;
      if (this.role_type === 'coach') {
        response = await api.getCoachLeaderFeedForwards(this.$route.params.id);
      } else {
        response = await api.getFeedforwards();
      }
      this.feedforwards = response.data;
    },

    async saveActionPlans(option) {
      try {
        let response;
        const data = { ...this.actionPlans };
        if (data.lga1 === null) delete data.lga1;
        if (data.lga2 === null) delete data.lga2;

        if (data.lga1) {
          data.lga1 = data.lga1.filter((el) => el.description !== '');
        }
        if (data.lga2) {
          data.lga2 = data.lga2.filter((el) => el.description !== '');
        }

        if (this.isNext) {
          if (!this.actionPlans.status && this.role_type === 'coach') {
            // Coach create next ap
            response = await api.createLeaderActionPlanNext(this.$route.params.id, data);
          } else if (!this.actionPlans.status && this.role_type === 'leader') {
            // Leader create next ap
            response = await api.createNextActionPlan(data);
          } else if (this.role_type === 'coach') {
            // Coach update next ap
            response = await api.updateLeaderActionPlanNext(this.$route.params.id, data);
          } else {
            // Leader update next ap
            response = await api.updateNextActionPlan(data);
          }
        } else if (!this.actionPlans.status && this.role_type === 'coach') {
          // Coach create ap
          response = await api.createLeaderActionPlan(this.$route.params.id, data);
        } else if (!this.actionPlans.status && this.role_type === 'leader') {
          // Leader create ap
          response = await api.createActionPlan(data);
        } else if (this.role_type === 'coach') {
          // Coach update ap
          response = await api.updateLeaderActionPlan(this.$route.params.id, data);
        } else {
          // Leader update ap
          response = await api.updateActionPlan(data);
        }

        // this.$emit('update:actionPlans', response.data);
        this.actionPlans = response.data;
        if (option !== 'publish') {
          this.$alert(
            this.$t('actionPlan.saved'),
            '', {
              type: 'success',
              confirmButtonText: this.$t('common.ok'),
              customClass: 'customMessage',
              roundButton: true,
            },
          );
        }
        if (this.role_type === 'leader') {
          this.fetchProcess();
        }
      } catch (err) {
        if (err.msg) {
          this.$message.error(err.msg);
        }
      }
    },

    async publishActionPlans() {
      // need to save first
      if (!this.canSave || !this.canLaunch) {
        this.unableDialog = true;
      } else {
        const confirmMsg = this.role_type === 'coach'
          ? this.$t('actionPlan.confirmPublishCoach') : this.$t('actionPlan.confirmPublish');
        this.$confirm(
          confirmMsg,
          '', {
            confirmButtonText: this.$t('common.yes'),
            cancelButtonText: this.$t('common.no'),
            customClass: 'customMessage',
            roundButton: true,
          },
        ).then(async () => {
          await this.saveActionPlans('publish');
          try {
            const data = { ...this.actionPlans };
            if (data.lga1 === null) delete data.lga1;
            if (data.lga2 === null) delete data.lga2;

            let response;
            if (this.role_type === 'coach') {
              response = await api.publishLeaderActionPlan(this.$route.params.id, data);
            } else {
              response = await api.publishActionPlan(data);
              this.fetchProcess();
            }
            const publishedMsg = this.role_type === 'coach'
              ? this.$t('actionPlan.publishedCoach') : this.$t('actionPlan.published');
            this.$alert(publishedMsg, '', {
              type: 'success',
              confirmButtonText: this.$t('common.close'),
              customClass: 'customMessage',
              roundButton: true,
              callback: () => {
                this.$emit('update:actionPlans', response.data);
                this.actionPlans = response.data;
                if (this.role_type === 'coach') {
                  this.$router.push({
                    name: 'myLeaderActionPlan',
                    params: {
                      id: this.$route.params.id,
                    },
                  });
                } else {
                  this.$router.push({ name: 'actionPlan' });
                }
              },
            });
          } catch (err) {
            if (err.msg === 'You must finish session 4 meeting to be able to publish action plan') {
              this.unableDialog = true;
            } else {
              this.$message.error(err.msg);
            }
          }
        }).catch((err) => {});
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  #actionPlanSetUp {
    .value {
      word-break: break-word;
    }

    .el-page-header {
      margin-bottom: 20px;
    }

    .wrapper.el-button {
      display: block;
      padding: 0;
      border: none;
      background-color: transparent;
    }

    .actionsBtn {
      display: flex;
      justify-content: center;
      flex-wrap: wrap;

      .mobileButton {
        width: calc(50% - 10px);
      }
    }

    .mHead {
      color: var(--themeFontColor);
      padding: 0;
    }

    .mobile {
      .title {
        padding: 0;
      }
    }
  }
</style>

<style lang="scss">
  #actionPlanSetUp {
    .el-tabs {
      margin-bottom: 20px;

      .el-tabs__header {
        .value {
          font-size: 16px;
          font-weight: bold;
        }
      }

      .el-tabs__content {
        padding: 20px 10px;
      }
    }
  }

  .customMessage {
    .el-message-box__content {
      display: flex;
      align-items: flex-start;

      .el-message-box__container {
        display: flex;
      }

      .el-message-box__status {
        position: relative !important;
        top: unset !important;
        transform: unset !important;
      }

      .el-message-box__message {
        padding-left: 12px;
      }
    }
  }
</style>
